import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiGet } from '../../api/client';
import { AiStatusCard, BackupCard, MediaHealthCard } from '../../components/admin/SiteSafetyPanel';

function syncHealthLabel(health) {
  switch (health) {
    case 'ok':
      return 'Healthy';
    case 'stale':
      return 'Stale (over 4h)';
    case 'error':
      return 'Last sync failed';
    default:
      return 'No sync yet';
  }
}

export default function AdminDashboard() {
  const [sync, setSync] = useState(null);
  const [counts, setCounts] = useState(null);

  useEffect(() => {
    apiGet('/admin/settings')
      .then(setSync)
      .catch(console.error);
    Promise.all([
      apiGet('/products').catch(() => []),
      apiGet('/reviews').catch(() => []),
      apiGet('/admin/tool-pages').catch(() => []),
      apiGet('/schedule').catch(() => []),
    ]).then(([products, reviews, toolPages, schedule]) => {
      const year = String(new Date().getFullYear());
      setCounts({
        tools: products.length,
        reviews: reviews.filter((r) => !(r.category || []).includes('Upcoming')).length,
        upcoming: reviews.filter((r) => (r.category || []).includes('Upcoming')).length,
        toolPages: toolPages.length,
        draftPages: toolPages.filter((p) => !p.published).length,
        planned: schedule.filter((e) => String(e.year) === year && !e.streamed).length,
      });
    });
  }, []);

  const lastSyncLabel = sync?.youtubeLastSync
    ? new Date(sync.youtubeLastSync).toLocaleString()
    : 'Never';

  return (
    <>
      <div className="admin-stat-grid">
        <Link to="/admin/catalog" className="admin-stat-card admin-stat-card--link">
          <strong>{counts?.tools ?? '…'}</strong>
          <span>Tools in the catalog</span>
        </Link>
        <Link to="/admin/reviews" className="admin-stat-card admin-stat-card--link">
          <strong>{counts?.reviews ?? '…'}</strong>
          <span>Published reviews{counts?.upcoming ? ` · ${counts.upcoming} upcoming` : ''}</span>
        </Link>
        <Link to="/admin/tool-pages" className="admin-stat-card admin-stat-card--link">
          <strong>{counts?.toolPages ?? '…'}</strong>
          <span>Tool pages{counts?.draftPages ? ` · ${counts.draftPages} draft` : ''}</span>
        </Link>
        <Link to="/admin/schedule" className="admin-stat-card admin-stat-card--link">
          <strong>{counts?.planned ?? '…'}</strong>
          <span>Games still planned this year</span>
        </Link>
      </div>

      <div className="admin-settings-grid admin-dashboard-safety">
        <BackupCard />
        <MediaHealthCard compact />
        <AiStatusCard />
      </div>

      <div className="admin-card admin-dashboard-sync">
        <h3>YouTube sync status</h3>
        {!sync ? (
          <p>Loading sync status…</p>
        ) : (
          <>
            <div className="admin-stat-chips">
              <div className="admin-stat-chip">
                <span className="admin-stat-value">{sync.videoCount ?? 0}</span>
                <span className="admin-stat-label">Videos in DB</span>
              </div>
            </div>
            <p className="admin-status-row">
              <span className={`admin-status-dot admin-status-dot--${sync.youtubeSyncHealth || 'unknown'}`} />
              {syncHealthLabel(sync.youtubeSyncHealth)}
            </p>
            <p><strong>Last sync:</strong> {lastSyncLabel}</p>
            {sync.youtubeLastSyncSource && (
              <p><strong>Source:</strong> {sync.youtubeLastSyncSource === 'cron' ? 'Automatic (cron)' : 'Manual'}</p>
            )}
            {sync.youtubeLastSyncCount > 0 && (
              <p><strong>Videos synced:</strong> {sync.youtubeLastSyncCount}</p>
            )}
            {sync.youtubeLastSyncError && (
              <p className="admin-message admin-message--error">{sync.youtubeLastSyncError}</p>
            )}
            <Link to="/admin/settings" className="btn btn-secondary btn-sm admin-dashboard-sync__link">
              Open YouTube sync settings
            </Link>
          </>
        )}
      </div>

      <div className="admin-card">
        <h3>Quick links</h3>
        <div className="admin-quick-links">
          <Link to="/admin/tool-pages" className="admin-quick-link">
            Tool Pages
            <small>Create & edit tool landing pages with steps, videos, FAQ</small>
          </Link>
          <Link to="/admin/catalog" className="admin-quick-link">
            Tool Catalog
            <small>Products on the Tools page & featured home section</small>
          </Link>
          <Link to="/admin/reviews" className="admin-quick-link">
            Game Reviews
            <small>Add ratings, posters, and review text</small>
          </Link>
          <Link to="/admin/stream-pages" className="admin-quick-link">
            Stream Pages
            <small>Schedule, Stream Info, Setup, Memes & custom pages</small>
          </Link>
          <Link to="/admin/pages" className="admin-quick-link">
            About & Stream Landing
            <small>Hero, offline message, and platform links for /stream</small>
          </Link>
          <Link to="/admin/schedule" className="admin-quick-link">
            Schedule Games
            <small>Calendar game entries by month</small>
          </Link>
          <Link to="/admin/streamers" className="admin-quick-link">
            Featured Streamers
            <small>Twitch list with live status from the API</small>
          </Link>
          <Link to="/admin/media" className="admin-quick-link">
            Media Library
            <small>Upload and manage images for the site</small>
          </Link>
          <Link to="/admin/settings" className="admin-quick-link">
            Settings
            <small>SEO, maintenance, users, backup & YouTube sync</small>
          </Link>
        </div>
      </div>
    </>
  );
}
